import { remoteHttpClientLayer } from "@t3tools/client-runtime/rpc";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import { FetchHttpClient, HttpClient, HttpClientRequest } from "effect/unstable/http";

import { getClerkToken, getOwnerId } from "../../clerkTokenBridge";
import { getPersistedGuestId } from "../../composioConnect";
import { readDesktopPrimaryBearerToken } from "./desktopAuth";

function isBrowserPrimary(): boolean {
  return (
    typeof window !== "undefined" &&
    window.desktopBridge === undefined &&
    window.location.origin.startsWith("http")
  );
}

function withPrimaryBearerToken(client: HttpClient.HttpClient): HttpClient.HttpClient {
  return client.pipe(
    HttpClient.mapRequestEffect((request) =>
      Effect.promise(readDesktopPrimaryBearerToken).pipe(
        Effect.map((bearerToken) =>
          bearerToken ? HttpClientRequest.bearerToken(request, bearerToken) : request,
        ),
      ),
    ),
  );
}

// Attach the current Clerk session JWT so a hosted (cross-origin) web client can
// authenticate to the API. The backend verifies it and mints a per-user browser
// session (see apps/server auth/http.ts autoGrantClerkSession). Same-origin
// browsers keep working too — the header is simply ignored when a cookie session
// already exists (loopback auto-grant).
function withClerkToken(client: HttpClient.HttpClient): HttpClient.HttpClient {
  return client.pipe(
    HttpClient.mapRequestEffect((request) =>
      Effect.promise(getClerkToken).pipe(
        Effect.map((token) =>
          token ? HttpClientRequest.setHeader(request, "x-clerk-token", token) : request,
        ),
      ),
    ),
  );
}

// Owner id must ride the Effect HTTP client itself. `installApiFetchAuth` patches
// `fetch` in main.tsx, but this layer can capture `globalThis.fetch` earlier, so
// the interceptor never sees shell-snapshot requests. Without this header the
// hosted snapshot returns zero threads and the socket resumes from that empty
// list — chats vanish on refresh even though they are still in the database.
function withOwnerHeaders(client: HttpClient.HttpClient): HttpClient.HttpClient {
  return client.pipe(
    HttpClient.mapRequestEffect((request) =>
      Effect.sync(() => {
        const ownerId = getOwnerId();
        const guestId = getPersistedGuestId();
        let next = request;
        if (ownerId) {
          next = HttpClientRequest.setHeader(next, "x-morrow-owner-id", ownerId);
        }
        if (guestId && ownerId && guestId !== ownerId) {
          next = HttpClientRequest.setHeader(next, "x-morrow-claim-guest-id", guestId);
        }
        return next;
      }),
    ),
  );
}

export function makePrimaryEnvironmentHttpLayer() {
  return Layer.unwrap(
    Effect.sync(() => {
      const baseLayer = remoteHttpClientLayer(globalThis.fetch);
      if (isBrowserPrimary()) {
        // Browser: credentialed requests so the backend session cookie sticks,
        // plus the Clerk token so a hosted deploy can establish that session.
        const clerkClientLayer = Layer.effect(
          HttpClient.HttpClient,
          Effect.map(HttpClient.HttpClient, (client) => withOwnerHeaders(withClerkToken(client))),
        ).pipe(Layer.provide(baseLayer));

        return Layer.merge(
          clerkClientLayer,
          Layer.succeed(FetchHttpClient.RequestInit, { credentials: "include" }),
        );
      }

      const bearerClientLayer = Layer.effect(
        HttpClient.HttpClient,
        Effect.map(HttpClient.HttpClient, (client) =>
          withOwnerHeaders(withPrimaryBearerToken(client)),
        ),
      ).pipe(Layer.provide(baseLayer));

      return Layer.merge(
        bearerClientLayer,
        Layer.succeed(FetchHttpClient.RequestInit, { credentials: "omit" }),
      );
    }),
  );
}

export const primaryEnvironmentHttpLayer = makePrimaryEnvironmentHttpLayer();
